import type {BaseComponentPart, StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';

/** Mirrors PHP `ProposedChangeStatus`. A stale card was refused because the saved value moved on after it was offered. */
export enum ProposedChangeStatus {
  PENDING = 'pending',
  APPLIED = 'applied',
  STALE = 'stale',
  FAILED = 'failed',
}

/** The element the card hands focus to once a tap settles, so a keyboard user keeps their place when the tapped control is swapped out. */
export enum ProposedChangeFocusTarget {
  APPLY = 'apply',
  UNDO = 'undo',
  EDIT = 'edit',
}

/** The error code the component-action endpoint answers with when the saved value moved on after the card was offered. */
export const STALE_COMPONENT_ACTION_ERROR_CODE = 'stale';

export const PROPOSED_CHANGE_ACTION_ENDPOINT = 'marketingagent/componentAction';

export const UNDO_COMPONENT_ACTION = 1;

export interface ProposedChangeValue {
  text: string;
  swatchHex: string | null;
  imageUrl: string | null;
}

export interface ProposedChange {
  label: string;
  before: ProposedChangeValue | null;
  after: ProposedChangeValue;
}

/** Every string the card shows, already translated server-side. */
export interface ProposedChangeLabels {
  apply: string;
  applied: string;
  undo: string;
  edit: string;
  stale: string;
  failed: string;
  undoFailed: string;
  readOnlyTitle: string;
}

/** The server also sends `actionPayload`; the card never reads it, because the endpoint reads it back from the stored card only. */
export interface ProposedChangeCardProps {
  title: string;
  appliedTitle: string;
  subtitle: string | null;
  rows: ProposedChange[];
  status: ProposedChangeStatus;
  labels: ProposedChangeLabels;
  /** Absolute URL of the page where the user makes the change themselves; opened in a new tab. */
  editUrl: string | null;
  actionKey: string;
}

export type ProposedChangeCardComponentPart = BaseComponentPart<StreamComponentType.PROPOSED_CHANGE_CARD, ProposedChangeCardProps>;

export interface ProposedChangeActionRequest {
  chatId: string;
  messageId: string;
  undo?: typeof UNDO_COMPONENT_ACTION;
}

/** How a tap settled: the server answered with the card's new status, or refused it. */
export enum ProposedChangeTapOutcome {
  SETTLED = 'settled',
  STALE = 'stale',
  FAILED = 'failed',
}

export interface SettledProposedChangeTap {
  outcome: ProposedChangeTapOutcome.SETTLED;
  status: ProposedChangeStatus;
}

export interface RefusedProposedChangeTap {
  outcome: ProposedChangeTapOutcome.STALE | ProposedChangeTapOutcome.FAILED;
}

export type ProposedChangeTapResult = SettledProposedChangeTap | RefusedProposedChangeTap;
